<template>
  <section class="rounded-3xl border border-gray-100 bg-white p-6 shadow-sm">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h2 class="text-lg font-semibold text-ink">Edit venue</h2>
        <p class="text-sm text-muted">Update venue details and pricing.</p>
      </div>
      <RouterLink to="/dashboard/venues" class="text-xs font-semibold text-brand">Back to venues</RouterLink>
    </div>

    <form class="mt-6 grid gap-4 sm:grid-cols-2" @submit.prevent="handleSubmit">
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Status</span>
        <select v-model="form.status" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Image URL</span>
        <input v-model.trim="form.img" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Type</span>
        <input v-model.trim="form.type" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Title</span>
        <input v-model.trim="form.title" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Per day price</span>
        <input v-model.number="form.per_day_price" type="number" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Rooms</span>
        <input v-model.number="form.rooms" type="number" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Latitude</span>
        <input v-model.trim="form.latitude" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Longitude</span>
        <input v-model.trim="form.longitude" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <label class="block sm:col-span-2">
        <span class="text-xs font-semibold uppercase tracking-wide text-muted">Address</span>
        <input v-model.trim="form.address" type="text" class="mt-2 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm text-ink shadow-sm focus:border-brand focus:outline-none">
      </label>
      <p v-if="error" class="sm:col-span-2 text-xs font-semibold text-red-600">{{ error }}</p>
      <div class="flex flex-wrap gap-3 sm:col-span-2">
        <button type="submit" class="rounded-full bg-brand px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:bg-brandDark" :disabled="loading">
          {{ loading ? 'Saving...' : 'Save changes' }}
        </button>
        <button type="button" class="rounded-full border border-red-200 px-4 py-2 text-xs font-semibold text-red-600 transition hover:border-red-300" :disabled="deleting" @click="handleDelete">
          {{ deleting ? 'Deleting...' : 'Delete venue' }}
        </button>
      </div>
    </form>
  </section>
</template>

<script setup>
import { reactive, ref } from 'vue'
import { RouterLink, useRouter } from 'vue-router'
import api from '@/services/api'
import { getAuthUserId } from '@/services/auth'

const router = useRouter()
const loading = ref(false)
const deleting = ref(false)
const error = ref('')
const form = reactive({
  venue_id: getAuthUserId(),
  status: 'active',
  img: '',
  type: '',
  title: '',
  per_day_price: '',
  rooms: '',
  latitude: '',
  longitude: '',
  address: ''
})

const handleSubmit = async () => {
  error.value = ''
  loading.value = true
  const payload = new FormData()
  Object.entries(form).forEach(([key, value]) => payload.append(key, value ?? ''))

  try {
    await api.updateVenue(payload)
    router.push('/dashboard/venues')
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || 'Failed to update venue'
  } finally {
    loading.value = false
  }
}

const handleDelete = async () => {
  deleting.value = true
  error.value = ''
  const payload = new FormData()
  payload.append('venue_id', form.venue_id)

  try {
    await api.deleteVenue(payload)
    router.push('/dashboard/venues')
  } catch (err) {
    error.value = err?.response?.data?.message || err?.message || 'Failed to delete venue'
  } finally {
    deleting.value = false
  }
}
</script>
